// External Dependencies:
import React from 'react'
import { connect } from 'react-redux'
import { localize } from 'i18n-calypso'
import Waypoint from 'react-waypoint'
import YouTubePlayer from 'react-player/lib/players/YouTube'
import VimeoPlayer from 'react-player/lib/players/Vimeo'
import FacebookPlayer from 'react-player/lib/players/Facebook'
import FilePlayer from 'react-player/lib/players/FilePlayer'
import screenfull from 'screenfull'

// Internal Dependencies:
import Image from 'church-theme-library/components/Image/Image'
import { getHhMmSsFromSeconds } from 'church-theme-library/utilities/time'

// Events:
const videoEnded = document.createEvent( 'Event' )
videoEnded.initEvent( 'videoEnded', true, true )

class MediaPlayer extends React.Component {
	static defaultProps = {
		url: '',
		id: '',
		service: ''
	}

	constructor( props ) {
		super( props )

		let service = props.service

		if ( ! service && props.url && props.url.indexOf( 'vimeo' ) > -1 ) {
			service = 'vimeo'
		} else if ( ! service && props.url && ( props.url.indexOf( 'youtube' ) > -1 || props.url.indexOf( 'youtu.be/' ) > -1 ) ) {
			service = 'youtube'
		}

		this.state = {
			hasStarted: false,
			isPlaying: false,
			inViewport: false,
			showVideo: false,
			vimeoStarted: false,
			hasFinished: false,
			played: 0,
			playedSeconds: 0,
			loaded: 0,
			duration: 0,
			service: service,
			isFullscreen: false,
			mouseIsHidden: false
		}

		this.playerRef = React.createRef()
		this.mousemoveTimeout = null
	}

	componentDidMount() {
		// Doesn't work on ios.
		if ( screenfull && 'function' == typeof screenfull.on ) {
			screenfull.on( 'change', () => {
				if ( screenfull.isFullscreen ) {
					document.addEventListener( 'mousemove', this.handleMouseMove )
				} else {
					document.removeEventListener( 'mousemove', this.handleMouseMove )
				}

				this.setState( {
					isFullscreen: screenfull.isFullscreen,
					mouseIsHidden: ! screenfull.isFullscreen
				} )
			} )
		}
	}

	componentWillUnmount() {

	}

	componentWillReceiveProps( nextProps ) {
		const { url } = this.props

		if ( url != nextProps.url ) {
			this.setState( {
				isPlaying: false,
				inViewport: false,
				showVideo: false,
				played: 0,
				loaded: 0,
				duration: 0
			} )
		}
	}

	onEnter = () => {
		if ( this.state.inViewport ) {
			return
		}

		this.setState( {
			inViewport: true,
			showVideo: true
		} )
	}

	handleMouseMove = () => {
		const { isFullscreen, mouseIsHidden } = this.state

		if ( ! isFullscreen ) {
			return
		}

		if ( mouseIsHidden ) {
			this.setState( {
				mouseIsHidden: false
			} )
		}

		clearTimeout( this.mousemoveTimeout )

		this.mousemoveTimeout = setTimeout( () => {
			this.setState( {
				mouseIsHidden: true
			} )
		}, 3000 )
	}

	handleWrapClick = ( e ) => {
		if ( this.wrap == e.target ) {
			this.setState( {
				isPlaying: ! this.state.isPlaying
			} )
		}
	}

	handleClick = ( e ) => {
		this.setState( {
			hasStarted: true,
			isPlaying: ! this.state.isPlaying
		} )
	}

	onPause = () => {
		this.setState( {
			isPlaying: false
		} )
	}

	onEnded = () => {
		this.setState( {
			isPlaying: false,
			hasFinished: true
		} )

		window.dispatchEvent( videoEnded )
	}

	onProgress = ( state ) => {
		const { vimeoStarted, duration, service } = this.state

		if ( ! this.state.seeking ) {
			this.setState( state )
		}

		// Only used when default controls are hidden. Not currently supported.
		// if ( 'vimeo' == service && ! vimeoStarted ) {
		// 	this.startVimeo()
		// }

		this.setState( {
			timeRemainingFormatted: getHhMmSsFromSeconds( duration - state.playedSeconds )
		} )
	}

	// Use background mode to hide the controls for vimeo.
	// Background will autoplay and mute - stop it.
	startVimeo = () => {
		const { hasStarted } = this.state

		if ( hasStarted || ! this.playerRef ) {
			return
		}

		let internalPlayer = this.playerRef.getInternalPlayer()

		if ( ! internalPlayer ) {
			return
		}

		// Set volume:
		internalPlayer.setVolume( 1 ).then( () => {
			// Pause:
			internalPlayer.pause().then( () => {
				internalPlayer.getPaused().then( ( paused ) => {
					if ( paused ) {
						// Rewind:
						internalPlayer.setCurrentTime( 0.0 ).then( () => {
							// Pause again:
							internalPlayer.pause().then( () => {
								this.setState( {
									vimeoStarted: true
								} )
							} )

							internalPlayer.setLoop( false )
						} )
					}
				} )
			} )
		} )
	}

	onDuration = ( duration ) => {
		this.setState( {
			duration: duration,
			timeRemainingFormatted: getHhMmSsFromSeconds( duration )
		} )
	}

	handleSeekMouseDown = ( e ) => {
		this.setState( {
			seeking: true
		} )
	}

	handleSeekChange = ( e ) => {
		const value = parseFloat( e.target.value )

		this.setState( {
			played: parseFloat( value )
		} )
	}

	handleSeekMouseUp = ( e ) => {
		const value = parseFloat( e.target.value )

		this.setState( {
			seeking: false
		} )

		this.playerRef.seekTo( value )
	}

	renderAudioPlayerControls = () => {
		const { image, translate } = this.props
		const { isPlaying, played, duration } = this.state
		const playClassName = isPlaying ? '-play-button is-playing' : '-play-button'
		const seekBarStyle = {
			width: `${played * 100}%`
		}

		// Get the time:
		const elapsed = ( duration * played )

		let durationFixed = duration.toFixed( 0 )
		let durationMinutes = Math.floor( durationFixed / 60 )
		let durationSeconds = durationFixed % 60
		durationSeconds = durationSeconds < 10 ? `0${durationSeconds}` : durationSeconds
		let durationDisplay = `${durationMinutes}:${durationSeconds}`

		let elapsedFixed = elapsed.toFixed( 0 )
		let elapsedMinutes = Math.floor( elapsedFixed / 60 )
		let elapsedSeconds = elapsedFixed % 60
		elapsedSeconds = elapsedSeconds < 10 ? `0${elapsedSeconds}` : elapsedSeconds
		let elapsedDisplay = `${elapsedMinutes}:${elapsedSeconds}`

		return (
			<div className="audio-player-controls">
				{ image && image.url &&
					<div onClick={ this.handleClick }>
						<Image
							thumb={ image.thumb }
							thumbWebp={ image.thumb_webp }
							url={ image.url }
							srcset={ image.srcset }
							srcsetWebp={ image.srcset_webp }
							sizes={ `(max-width: ${siteData.mobile_max_width}px) 100vw` }
							alt={ image.alt }
							focalPoint={ image.focal_point }
							width={ image.width }
							height={ image.height }
						/>
					</div>
				}

				<div className="-inner">
					<div className={ playClassName } onClick={ this.handleClick }>
						<svg x="0px" y="0px" viewBox="0 0 213.7 213.7" >
							<title>{ translate( 'Play' ) }</title>

							<circle className="-circle" fill="none" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" cx="106.8" cy="106.8" r="103.3"></circle>

							<polygon className="-triangle" fill="none" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" points="73.5,62.5 148.5,105.8 73.5,149.1 "></polygon>

							<g className="-pause">
								<rect x="78" y="64" rx="5" ry="5" height="85" width="10" />
								<rect x="124" y="64" rx="5" ry="5" height="85" width="10" />
							</g>
						</svg>
					</div>

					<div className="-time">
						{ elapsedDisplay } / { durationDisplay }
					</div>

					<div className="-seek-bar">
						<div className="-inner">
							<div className="-bar-progress">
								<div className="-bar-progress-inner" style={ seekBarStyle } />
							</div>

							<input
								className="-bar-input"
								type="range" min={ 0 } max={ 1 } step="any"
								value={ played }
								onMouseDown={ this.handleSeekMouseDown }
								onChange={ this.handleSeekChange }
								onMouseUp={ this.handleSeekMouseUp }
							/>
						</div>
					</div>
				</div>
			</div>
		)
	}

	handlePlay = () => {
		const { service } = this.state

		this.setState( {
			isPlaying: true,
			hasStarted: true
		} )

		if ( 'youtube' == service ) {
			this.setState( {
				showYouTubeCover: true
			} )

			setTimeout( () => {
				this.setState( {
					showYouTubeCover: false
				} )
			}, 800 )
		}

		if ( 'vimeo' == service ) {
			let internalPlayer = this.playerRef.getInternalPlayer()

			if ( internalPlayer ) {
				internalPlayer.play()
			}
		}
	}

	handlePause = () => {
		const { service } = this.state

		this.setState( {
			isPlaying: false
		} )

		if ( 'vimeo' == service ) {
			let internalPlayer = this.playerRef.getInternalPlayer()

			if ( internalPlayer ) {
				internalPlayer.pause()
			}
		}
	}

	handleFullscreenEnter = () => {
		if ( ! screenfull.isEnabled ) {
			return
		}

		screenfull.request( this.wrap )
	}

	handleFullscreenExit = () => {
		if ( ! screenfull.isEnabled ) {
			return
		}

		screenfull.exit()
	}

	render() {
		let { translate, wrapStyle, thumb_url, image, id, url, file, type, width, height, aspectRatio } = this.props
		let { showVideo, hasStarted, isPlaying, service, played, loaded, timeRemainingFormatted, isFullscreen, mouseIsHidden, showYouTubeCover } = this.state

		if ( this.props.playing ) {
			isPlaying = this.props.playing
		}

		let wrapClass = isPlaying ? 'media-player is-playing' : 'media-player is-not-playing'
		wrapClass += ` is-service-${service}`

		if ( hasStarted || isPlaying ) {
			wrapClass += ' has-started'
		}

		if ( isFullscreen ) {
			wrapClass += ' is-fullscreen'
		}

		if ( isFullscreen && mouseIsHidden ) {
			wrapClass += ' is-mouse-hidden'
		}

		if ( ! isPlaying || showYouTubeCover ) {
			wrapClass += ' with-youtube-cover'
		}

		if ( 'audio' == type ) {
			wrapClass += ' audio-player'
		}

		let video,
			showControls = false,
			loop = false,
			muted = false,
			volume = 1

		if ( this.props.controls ) {
			showControls = this.props.controls
		}

		if ( this.props.loop ) {
			loop = this.props.loop
		}

		if ( this.props.muted ) {
			muted = this.props.muted
		}

		if ( this.props.volume ) {
			volume = this.props.volume
		}

		// Note: File type will only be used if no other service is found.
		// Allow passing a file url via the url attribute.
		if ( ! file ) {
			file = url
		}

		if ( ! url ) {
			if ( id && 'youtube' == service ) {
				url = `https://www.youtube.com/watch?v=${id}`
			} else if ( id && 'vimeo' == service ) {
				url = `https://vimeo.com/${id}`
			}
		}

		// If there is a thumbnail image available,
		// don't load the video until interaction/play.
		if ( 'vimeo' != service && thumb_url && ! isPlaying && ! hasStarted ) {
			showVideo = false
		}

		if ( url ) {
			if ( 'vimeo' == service ) {
				// Hiding the native controls is only supported for plus accounts or higher.
				showControls = false // The custom controls.
				video = (
					<VimeoPlayer
						ref={ ( e ) => { this.playerRef = e } }
						playing={ isPlaying }
						url={ url }
						controls={ ! showControls }
						onPause={ this.onPause }
						onEnded={ this.onEnded }
						onProgress={ this.onProgress }
						onDuration={ this.onDuration }
						config={ {
							vimeo: {
								playerOptions: {
									background: showControls
								}
							}
						} }
						loop={ loop }
						muted={ muted }
						volume={ volume }
					/>
				)
			} else if ( 'youtube' == service ) {
				showControls = true
				video = (
					<YouTubePlayer
						ref={ ( e ) => { this.playerRef = e } }
						playing={ isPlaying }
						url={ url }
						controls={ false }
						onPause={ this.onPause }
						onEnded={ this.onEnded }
						onProgress={ this.onProgress }
						onDuration={ this.onDuration }
						config={ {
							youtube: {
								playerVars: {
									controls: 0,
									modestbranding: 1,
									rel: 0
								}
							}
						} }
						loop={ loop }
						muted={ muted }
						volume={ volume }
					/>
				)
			} else if ( 'facebook' == service ) {
				showControls = true
				video = (
					<FacebookPlayer
						ref={ ( e ) => { this.playerRef = e } }
						playing={ isPlaying }
						url={ url }
						controls={ false }
						onPause={ this.onPause }
						onEnded={ this.onEnded }
						onProgress={ this.onProgress }
						onDuration={ this.onDuration }
						config={ {
							facebook: {
								appId: siteData.vl_facebook_app_id
							}
						} }
						loop={ loop }
						muted={ muted }
						volume={ volume }
					/>
				)
			}
		}

		if ( file && ! video ) {
			let forceAudio = 'audio' == type ? true : false
			showControls = 'audio' == type ? false : true

			video = (
				<FilePlayer
					ref={ ( e ) => { this.playerRef = e } }
					playing={ isPlaying }
					url={ file }
					controls={ false }
					onPause={ this.onPause }
					onEnded={ this.onEnded }
					onProgress={ this.onProgress }
					onDuration={ this.onDuration }
				/>
			)
		}

		return (
			<div className={ wrapClass } style={ wrapStyle } onClick={ this.handleWrapClick } ref={ ( e ) => { this.wrap = e } }>
				<Waypoint onEnter={ this.onEnter } />

				{ showVideo && video }

				{ showControls && thumb_url &&
					<div className="-poster">
						<img src={ thumb_url } alt="" />
					</div>
				}

				{ showControls &&
					<button className="-play-wrap" onClick={ this.handlePlay } aria-label={ translate( 'Play' ) }>
						<div className="-play">
							<svg viewBox="0 0 18 18">
								<path d="M15.562 8.1L3.87.225C3.052-.337 2 .225 2 1.125v15.75c0 .9 1.052 1.462 1.87.9L15.563 9.9c.584-.45.584-1.35 0-1.8z"></path>
							</svg>
						</div>
					</button>
				}

				{ hasStarted && showControls &&
					<div className="-controls">
						<div className="-youtube-bg-top"></div>

						<div className="-inner">
							<div className="-play-pause">
								<button className="-play" onClick={ this.handlePlay }>
									<svg viewBox="0 0 18 18">
										<path d="M15.562 8.1L3.87.225C3.052-.337 2 .225 2 1.125v15.75c0 .9 1.052 1.462 1.87.9L15.563 9.9c.584-.45.584-1.35 0-1.8z"></path>
									</svg>
								</button>

								<button className="-pause" onClick={ this.handlePause }>
									<svg viewBox="0 0 18 18">
										<rect x="0" y="0" rx="2" ry="2" height="16" width="5" />
										<rect x="8" y="0" rx="2" ry="2" height="16" width="5" />
									</svg>
								</button>
							</div>

							<div className="-track-wrap">
								<input
									className="-input"
									type="range" min={ 0 } max={ 1 } step="any"
									value={ played }
									onMouseDown={ this.handleSeekMouseDown }
									onTouchStart={ this.handleSeekMouseDown }
									onChange={ this.handleSeekChange }
									onTouchMove={ this.handleSeekChange }
									onMouseUp={ this.handleSeekMouseUp }
									onTouchEnd={ this.handleSeekMouseUp }
								/>

								<div className="-progress">
									<div className="-progress-played" style={ { width: `${played * 100}%` } } max="100"></div>
									<div className="-progress-buffer" style={ { width: `${loaded * 100}%` } } max="100"></div>
								</div>
							</div>

							<div className="-time">
								<span>{ timeRemainingFormatted }</span>
							</div>

							<div className="-fullscreen">
								<button className="-enter-fullscreen" onClick={ this.handleFullscreenEnter }>
									<svg viewBox="0 0 18 18">
										<path d="M10 3h3.6l-4 4L11 8.4l4-4V8h2V1h-7zM7 9.6l-4 4V10H1v7h7v-2H4.4l4-4z"></path>
									</svg>
								</button>

								<button className="-exit-fullscreen" onClick={ this.handleFullscreenExit }>
									<svg viewBox="0 0 18 18">
										<path d="M1 12h3.6l-4 4L2 17.4l4-4V17h2v-7H1zM16 .6l-4 4V1h-2v7h7V6h-3.6l4-4z"></path>
									</svg>
								</button>
							</div>
						</div>
					</div>
				}

				{ 'audio' == type &&
					this.renderAudioPlayerControls()
				}
			</div>
		)
	}
}

export default localize( MediaPlayer )
